'use client';

import { AppProgressProvider as ProgressProvider } from '@bprogress/next';
import logger from 'logger';
import { ErrorInfo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { addError, Errors } from 'rum';
import { Box, ErrorFallback, Footer } from 'ui';
import { useTheme } from 'ui/hooks';

import { ResponseError } from '~/data/api';

import { ContactForm } from '../../../contactForm/components/ContactForm';
import { AppBar } from '../AppBar';
import Sidebar from '../Sidebar/Sidebar';
import { getMaintenanceMessage, MaintenanceBanner } from './MaintenanceBanner';
import { OnboardingBanner } from './OnboardingBanner';

interface FallbackComponentProps {
  error: Error | ResponseError | null;
}

const onError = (error: Error, info: ErrorInfo) => {
  logger.error('error :', error);
  addError(Errors.UNHANDLED_NAVIGATION_LAYOUT_ERROR, error, {
    info,
  });
};

const FallbackComponent = ({ error }: FallbackComponentProps) => {
  const { t } = useTranslation('common');

  const supportEmail = t('supportEmail');

  if (error instanceof ResponseError && error.response.status === 404) {
    return (
      <ErrorFallback
        showLogo={false}
        title={t('errorPage.notFound.title')}
        description={t('errorPage.notFound.description')}
        supportEmail={supportEmail}
      />
    );
  }

  return (
    <ErrorFallback
      showLogo={false}
      title={t('errorPage.default.title')}
      description={t('errorPage.default.description')}
      supportEmail={supportEmail}
    />
  );
};

const NavigationLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <Box
      display="flex"
      sx={(theme) => ({
        backgroundColor: theme.backgrounds.page,
        height: theme.dimensions.pageHeight,
      })}
    >
      <ProgressProvider color={palette.primary.main} options={{ showSpinner: false }} shallowRouting>
        <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

        <Box
          sx={{
            height: '100%',
            overflow: 'auto',
            width: '100%',
          }}
        >
          <AppBar toggleSidebar={toggleSidebar} />

          <OnboardingBanner />

          <Box
            data-testid="navigation-layout-content"
            px={{ lg: 6, xs: 2 }}
            pt={2.5}
            pb={2.5}
            sx={{
              minHeight: '100%',
            }}
          >
            <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
              <MaintenanceBanner />
              {!getMaintenanceMessage() && children}
            </ErrorBoundary>
          </Box>
          <Footer email={t('supportEmail')} />
          <ContactForm />
        </Box>
      </ProgressProvider>
    </Box>
  );
};

export default NavigationLayout;
